import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { convertBoolean } from "go-core/components/Formatters/FormatBoolean";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { partnerApi } from "../../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerSummaryApi } from "../../../../../../../../../../services/Api/Partner/partnerTypes";
import { selectOrganization } from "../../../../../../../../../../services/organizations/selectors";
import { selectPartner } from "../../../../../../../../../../services/partner/selectors";
import { downloadFile } from "../../../../../../../../../../utils";
import SendViaEmail from "../../../../../../../Admin/pages/Payoffs/pages/components/SendViaEmail";

interface ListState {
	resource?: Record<string, any>;
	partnerId: number;
}

const listName = "PARTNER_PAYOFF";
const resourceType = "PAYOFF";

const List = ({ resource, partnerId }: ListState) => {
	const { t } = useTranslation("admin");
	const { addFlash } = useFlash();
	const location = useLocation();
	const organization = useSelector(selectOrganization);
	const [items, setItems] = useState<PartnerSummaryApi[]>([]);
	const [summarizeLoading, setSummarizeLoading] = useState(false);
	const [showModal, setShowModal] = useState<undefined | PartnerSummaryApi>(undefined);
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;

	if (!resource) return null;
	const data = resource.read();

	const onSummarizeHandler = async () => {
		setSummarizeLoading(true);
		try {
			await partnerApi.summarizePayoffs(partnerId);
			if (config.fetch) {
				const res = await config.fetch({});
				setItems(res);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setSummarizeLoading(false);
	};

	const drawDate = (item: PartnerSummaryApi) => {
		if (item.date) {
			const month = `${Number(item.date[1]) < 10 ? `0${item.date[1]}` : item.date[1]}`;
			return `${month}.${item.date[0]}`;
		}
		return "";
	};

	const handleDownloadPdf = async (item: PartnerSummaryApi) => {
		await downloadFile(
			() => partnerApi.getSummaryPdf(item.id),
			`${t("common.word.payoff")}_${item.number}.pdf`,
			addFlash
		);
	};

	let config = {
		fields: [
			{
				id: "number",
				type: "text",
				name: t("lib:common.word.number"),
				render: (item: PartnerSummaryApi) => <Link to={`${location.pathname}/${item.id}`}>{item.number}</Link>,
				renderExport: (item: PartnerSummaryApi) => item.number,
			},
			{
				id: "date",
				type: "date",
				name: t("lib:common.word.date"),
				render: (item: PartnerSummaryApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{drawDate(item)}</Link>
				),
				renderExport: (item: PartnerSummaryApi) => drawDate(item),
			},
			{
				id: "email",
				type: "text",
				name: t("lib:common.word.email"),
				render: (item: PartnerSummaryApi) => item.partner?.company_email,
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					OPEN: t("enums.common.status.OPEN"),
					CLOSED: t("enums.common.status.CLOSED"),
				},
				render: (item: PartnerSummaryApi) => <FormatResourceStatus status={item.status} />,
			},
			{
				id: "email_sent",
				type: "boolean",
				name: `${t("modules.payoff.field.email_sent.title")}`,
				render: (item: PartnerSummaryApi) => convertBoolean(item.email_sent, t),
			},
			{
				id: "provision_value",
				type: "number",
				name: t("common.word.provision_value"),
				render: (item: PartnerSummaryApi) => `${FormatMoney(item.provision_value)}`,
			},
		],
		selectedColumns: ["number", "email", "email_sent", "date", "status", "provision_value"],
		actions: [
			{
				name: t("common.word.details"),
				link: (item: PartnerSummaryApi) => `${location.pathname}/${item.id}`,
			},
			{
				name: `${t("common.action.download_pdf")}`,
				click: (item: PartnerSummaryApi) => handleDownloadPdf(item),
			},
			{
				name: t("common.action.send_pdf"),
				click: (item: PartnerSummaryApi) => setShowModal(item),
			},
		],
		segments: [
			{
				id: "all",
				slug: "all",
				name: t("common.word.all", { ns: "lib" }),
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return partnerApi.getPayoffs(partnerId, params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.payoff.header.title"),
			filename: t("modules.payoff.field.export_config.filename"),
			organization: `${organization.name}`,
		},
		numberOfStickyColumnsAtTheStart: 2,
		doesIdColumnRedirectToPreviewPage: true,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	const buttons: ButtonProps[] = [
		{
			variant: "primary",
			title: t("modules.payoff.action.payoff.title"),
			action: onSummarizeHandler,
			loading: summarizeLoading,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("modules.payoff.action.payoff.title"),
			action: onSummarizeHandler,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.payoff.header.title")} buttons={buttons} />}
			<ListData
				data={items}
				config={config}
				onFetch={(items: PartnerSummaryApi[]) => setItems(items)}
				mobileActions={mobileActions}
			/>
			{showModal && <SendViaEmail onHide={() => setShowModal(undefined)} summary={showModal} />}
		</>
	);
};

const PartnerPreviewPayoffIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const partnerId = useSelector(selectPartner)?.id;
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation("admin");

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.payoff.header.title")} | ${t("modules.partner.field.partner_panel.title")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} partnerId={Number(partnerId)} />
		</Suspense>
	);
};
export default PartnerPreviewPayoffIndexPage;
